import type { WikiManifestPage } from '../api/wiki.js';

export interface WikiTreeNode {
  readonly name: string;
  readonly fullPath: string;
  readonly children: WikiTreeNode[];
  page?: WikiManifestPage;
}

export function detectSeparator(pages: readonly WikiManifestPage[]): '/' | '.' {
  return pages.some((p) => p.title.includes('/')) ? '/' : '.';
}

export function buildWikiTree(pages: readonly WikiManifestPage[]): WikiTreeNode[] {
  const separator = detectSeparator(pages);
  const roots: WikiTreeNode[] = [];

  for (const page of pages) {
    const segments = page.title.split(separator).filter((s) => s.length > 0);
    if (segments.length === 0) continue;

    let level = roots;
    let pathAcc = '';

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]!;
      pathAcc = pathAcc ? `${pathAcc}${separator}${seg}` : seg;
      const isLeaf = i === segments.length - 1;

      let node = level.find((n) => n.name === seg);
      if (!node) {
        node = { name: seg, fullPath: pathAcc, children: [] };
        level.push(node);
      }

      if (isLeaf) {
        node.page = page;
      }

      level = node.children;
    }
  }

  return sortTree(roots);
}

function sortTree(nodes: WikiTreeNode[]): WikiTreeNode[] {
  nodes.sort((a, b) => {
    const aFolder = a.children.length > 0 && !a.page;
    const bFolder = b.children.length > 0 && !b.page;
    if (aFolder !== bFolder) return aFolder ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  for (const n of nodes) sortTree(n.children);
  return nodes;
}

export function collectAncestorPaths(
  nodes: readonly WikiTreeNode[],
  targetSlug: string,
  trail: string[] = [],
  out = new Set<string>(),
): Set<string> {
  for (const node of nodes) {
    const nextTrail = [...trail, node.fullPath];
    if (node.page?.slug === targetSlug) {
      for (const p of trail) out.add(p);
      return out;
    }
    if (node.children.length > 0) {
      const before = out.size;
      collectAncestorPaths(node.children, targetSlug, nextTrail, out);
      if (out.size > before) return out;
    }
  }
  return out;
}
